import { useHealthblocksSWR } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { mixpanel } from '@/mixpanel'
import { QuestionnaireAnswers } from '@/modules/questionnaire/components/questionnaire-answers/QuestionnaireAnswers'
import { RelatedTriggers } from '@/modules/shared/components/related-items/RelatedTriggers'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { isIntramuros } from '@/utils/intramuros'
import { useTaskStatusMetaData } from '../../hooks/useTaskStatusMetaData'
import { buildQuestionnaireTaskUrl } from '../../utils/task.utils'
import { StTaskDetailParagraph, TaskDetailLayout } from '../detail-layout/TaskDetailLayout'
import { QuestionnaireReconfirmDetail } from './QuestionnaireReconfirmDetail'

import type { IQuestionnaireWithHealthParameters } from 'index'
import type { QuestionnaireTask } from '../../types/task.types'

type Props = {
  task: QuestionnaireTask
  refetch?: () => void
}

export const QuestionnaireDetail = ({ task, refetch }: Props) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const navigate = useNavigate()
  const isLoggedInForMandatee = useLoggedInForMandatee()
  const { daysTillExpiry } = useTaskStatusMetaData(task)
  const { data: awellHostedPages, isLoading: isLoadingFeatureFlags } = useHealthblocksSWR<{
    enabled: boolean
  }>('/feature-flags/awell-hosted-pages')

  // Template variables
  // ------------------------------------------------------------------------- /

  const isCompleted = task.status === 'completed'
  const isExpired = task.status === 'expired'
  const isReconfirmation = task.reconfirmation ?? false
  const isPrefill = task.prefill ?? false

  const statusDisplay = useMemo((): string => {
    if (isCompleted) {
      return i18n._(globalDefinitions.task.Questionnaire.completed, {
        date: formatLocale(task.updatedAt, 'dd/MM/yyyy'),
      })
    }

    if (isExpired) {
      return i18n._(globalDefinitions.task.expired)
    }

    if (daysTillExpiry === 0) {
      return i18n._(globalDefinitions.task.Questionnaire.expires_today)
    }

    if (daysTillExpiry === 1) {
      return i18n._(globalDefinitions.task.Questionnaire.expires_in_one_day)
    }

    if (daysTillExpiry && daysTillExpiry > 1) {
      return i18n._(globalDefinitions.task.Questionnaire.expires_in_x_days, {
        count: daysTillExpiry,
      })
    }

    return i18n._(globalDefinitions.task.no_expiry, {
      date: formatLocale(task.createdAt, 'dd/MM/yyyy'),
    })
  }, [isCompleted, isExpired, daysTillExpiry, task.updatedAt, task.createdAt])

  const description = useMemo(() => {
    if (isCompleted) {
      return t({
        id: 'questionnaire-detail.info.completed',
        message:
          'Je hebt deze vragenlijst ingevuld op {date}. Je kan hier geen wijzigingen meer in aanbrengen. Neem bij vragen of problemen contact op met je zorgteam.',
        values: {
          date: formatLocale(task.updatedAt, 'dd/MM/yyyy'),
        },
      })
    }

    if (isExpired) {
      return (
        task.message ??
        t({
          id: 'questionnaire-detail.info.expired',
          message:
            'Deze vragenlijst werd helaas niet tijdig ingevuld en is intussen afgesloten.\n\nBij vragen of bezorgdheden kan je contact opnemen met je zorgteam.',
        })
      )
    }

    if (task.message) {
      return task.message
    }

    return t({
      id: 'questionnaire-detail.info.placeholder',
      message: 'Vul deze vragenlijst in.',
    })
  }, [isCompleted, isExpired, task.updatedAt, task.message])

  const ctaText = useMemo(() => {
    if (isExpired && isIntramuros) {
      return t({
        id: 'questionnaire-detail.cta.start',
        message: 'Vul vragenlijst in',
      })
    }

    if (isCompleted || isExpired) {
      return i18n._(globalDefinitions.close)
    }

    return t({
      id: 'questionnaire-detail.cta.start',
      message: 'Vul vragenlijst in',
    })
  }, [isExpired, isIntramuros, isCompleted])

  // Methods
  // ------------------------------------------------------------------------- /

  const handleCtaPress = async () => {
    if (isLoadingFeatureFlags) return

    // Check if intramuros and not completed, then navigate to the questionnaire
    if (isIntramuros && isExpired) {
      mixpanel.track('questionnaire_intramuros_start_expired', {
        is_mandate: isLoggedInForMandatee,
      })
      const url = buildQuestionnaireTaskUrl(task, awellHostedPages?.enabled ?? false)
      if (url) {
        navigate(url)
      }

      return
    }

    // If completed or expired, navigate back to the task list
    if (isCompleted || isExpired) {
      navigate('..', { state: { taskId: task.id } })
      return
    }

    const url = buildQuestionnaireTaskUrl(task, awellHostedPages?.enabled ?? false)

    if (url) {
      navigate(url)
    }
  }

  // Render
  // ------------------------------------------------------------------------- /

  if ((isReconfirmation || isPrefill) && !isCompleted) {
    return (
      <QuestionnaireReconfirmDetail
        task={task}
        statusDisplay={statusDisplay}
        refetch={refetch}
        variant={isPrefill ? 'prefill' : 'reconfirmation'}
      />
    )
  }

  const renderContent = () => {
    if (isCompleted) {
      return (
        <FlexColumn gap={theme.UI.Spacing.Space4}>
          {/* Task description  */}
          <StMessage>{description}</StMessage>

          {/* Render Answers */}

          <Line />
          <QuestionnaireAnswers
            questionnaire={task.questionnaire as IQuestionnaireWithHealthParameters}
            questionnaireResponse={task.questionnaireResponse}
          />

          {task?.trigger && <RelatedTriggers trigger={task.trigger} />}
        </FlexColumn>
      )
    }

    return (
      <FlexColumn gap={theme.UI.Spacing.Space4}>
        <StMessage>{description}</StMessage>
        {task?.trigger && <RelatedTriggers trigger={task.trigger} />}
      </FlexColumn>
    )
  }

  return (
    <StTaskDetailLayout>
      <TaskDetailLayout.Header task={task} statusLabel={statusDisplay} />

      <TaskDetailLayout.Content>{renderContent()}</TaskDetailLayout.Content>

      <TaskDetailLayout.Footer>
        <Button variant='Primary' onClick={handleCtaPress} isLoading={isLoadingFeatureFlags}>
          {ctaText}
        </Button>
      </TaskDetailLayout.Footer>
    </StTaskDetailLayout>
  )
}

const StTaskDetailLayout = styled(TaskDetailLayout)`
  @media ${breakpoints.fromL} {
    /* Always show a thin scrollbar on the content area to hint at scrollable content.
       Targets StContentContainer (last div child — footer is a <footer> element). */
    > div:last-of-type {
      overflow-y: scroll;
      scrollbar-width: thin;
      scrollbar-color: ${({ theme }) => theme.Colors.Neutrals.Light} transparent;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background-color: ${({ theme }) => theme.Colors.Neutrals.Light};
        border-radius: 3px;
      }
    }
  }
`

const StMessage = styled(StTaskDetailParagraph)`
  white-space: pre-wrap;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space2} 0;
`
